import type { ComponentPublicInstance } from 'vue';

// PRODUCT-26110 Phase 2B2: 'vue-router/types/router' is a vue-router-3-only subpath,
// not present in v4's `exports` map at all -- module-not-found, not just a type
// mismatch. `RawLocation` -> `RouteLocationRaw`.
import { createDecorator } from 'vue-facing-decorator';
import type { RouteLocationRaw, RouteLocationNormalized } from 'vue-router';
// PRODUCT-26110 Phase 2B2 Task 6b: migrated off `vue-class-component` (no longer a
// dependency anywhere in the workspace since Task 1's dependency bump -- this file was
// the last real production import of it, previously papered over by a jest
// moduleNameMapper stub). `vue-facing-decorator` exports its own `createDecorator` with
// the same `(creator: (options, key) => void) => PropertyDecorator` shape: the `options`
// argument it hands the creator is the fully-built Vue component options object
// (`created`/`unmounted`/`methods` already populated from the class's own lifecycle
// methods and other decorators -- see `vue-facing-decorator/dist/component.js`'s
// `buildComponent`, which applies registered custom-decorator creators against the real
// `option` object right before `defineComponent(option)`), so `addRouteChangeListener`'s
// options-mutation logic below is unchanged.

type Vue = ComponentPublicInstance;

type DecoratorFactory = Parameters<typeof createDecorator>[0];
type Vm = Parameters<DecoratorFactory>[0];

export type RawNavigationResult = RouteLocationRaw | false | void;
export type NavigationResult = RawNavigationResult | Promise<RawNavigationResult>;

function addRouteChangeListener(options: Vm, key: string) {
    // Store listener destroyers outside of the original component instance
    const listeners = new WeakMap<Vue, Function>();

    // Subscribe to router changes on instance creation
    const originalCreated = options.created;
    options.created = function created(this: Vue): void {
        const destroyListener = this.$router.beforeEach((to, from, next) => {
            // @ts-ignore: calling unknown method
            Promise.resolve(this[key](to, from)).then(next);
        });
        listeners.set(this, destroyListener);
        if (originalCreated) {
            originalCreated.call(this);
        }
    };

    // Unsubscribe from router changes after instance is unmounted
    const originalUnmounted = options.unmounted;
    options.unmounted = function unmounted(this: Vue): void {
        const destroyListener = listeners.get(this);
        if (destroyListener) {
            destroyListener();
        }
        if (originalUnmounted) {
            originalUnmounted.call(this);
        }
    };
}

// PRODUCT-26110 Phase 2B2: `{ preserve: true }` is REQUIRED under vue-facing-decorator.
// Unlike vue-class-component (which left every class method in the built `methods`
// option regardless of decorators), vfd's `methodsAndHooks` builder runs the decorated
// method name through `excludeNames`, which DROPS any name registered in the
// `customDecorator` slot map unless at least one of its entries was created with
// `preserve: true` (see vue-facing-decorator/dist/utils.js `excludeNames`). Without it,
// the decorated method (e.g. ProcedureTarget#onLeave) never lands on the instance, so
// the `this[key](...)` call inside the beforeEach guard below throws
// `TypeError: this[key] is not a function`, which aborts initial router navigation and
// prevents the auth-redirect guard from ever running. Also needed by
// beforeRouteNameChange, which reads `methods[key]` at decoration time.
export const beforeRouteChange = createDecorator(addRouteChangeListener, { preserve: true });

export const beforeRouteNameChange = createDecorator((options, key) => {
    const methods = options.methods!;

    // Override original method to check for proper route name
    const originalMethod = methods[key] as Function;
    methods[key] = function onRouteNameChange(to: RouteLocationNormalized, from: RouteLocationNormalized) {
        if (to?.name !== from?.name) {
            return originalMethod.call(this, to, from);
        }
    };

    // For better logging, retrieve back original name
    Object.defineProperty(methods[key], 'name', { value: `decorated_${key}`, enumerable: false });

    // Attach regular route listener
    addRouteChangeListener(options, key);
}, { preserve: true });
